/* eslint-disable no-restricted-syntax -- WhatsApp UI mock: it copies WhatsApp's own colors, not theme roles. */
import {
  BookIcon,
  Building2Icon,
  CirclePlayIcon,
  ExternalLinkIcon,
  ImageIcon,
  MicIcon,
  PhoneIcon,
  PlusIcon,
  ReplyIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { resolveWhatsAppParameters } from '@/modules/campaigns/utils/utils';
import { getComponentsFromTemplate } from '@/modules/whatsapp/utils';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { Option } from '@/shared/types/rules';

type TemplateParameter = { name?: string | Option };

interface WhatsAppPhonePreviewProps {
  template: WhatsappTemplate;
  headerParameters?: TemplateParameter[];
  bodyParameters?: TemplateParameter[];
  footerParameters?: TemplateParameter[];
  phoneNumber?: string | null;
  subscriberContext?: Record<string, unknown>;
  className?: string;
}

const MediaHeader = ({
  mediaInfo,
}: {
  mediaInfo: { format: 'image' | 'video' | 'document'; exampleUrl?: string };
}) => {
  if (!mediaInfo.exampleUrl) {
    return null;
  }

  switch (mediaInfo.format) {
    case 'image':
      return (
        <div className="mb-2 overflow-hidden rounded-t">
          <img
            src={mediaInfo.exampleUrl}
            alt=""
            className="h-auto max-h-[200px] w-full object-cover"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'flex';
              }
            }}
          />
          <div className="mb-2 hidden items-center gap-2 rounded bg-subtle p-2">
            <ImageIcon size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">image.jpg</span>
          </div>
        </div>
      );
    case 'video':
      return (
        <div className="mb-2 overflow-hidden rounded-t">
          <video
            src={mediaInfo.exampleUrl}
            controls
            className="h-auto max-h-[200px] w-full"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
              const fallback = e.currentTarget.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'flex';
              }
            }}
          />
          <div className="mb-2 hidden items-center gap-2 rounded bg-subtle p-2">
            <CirclePlayIcon size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">video.mp4</span>
          </div>
        </div>
      );
    case 'document':
      return (
        <div className="mb-2 flex items-center gap-2 rounded bg-subtle p-2">
          <BookIcon size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">document.pdf</span>
        </div>
      );
    default:
      return null;
  }
};

const WhatsAppButton = ({
  button,
  isLast,
}: {
  button: { type: string; text: string; url?: string };
  isLast: boolean;
}) => {
  const icon = (() => {
    switch (button.type) {
      case 'URL':
        return <ExternalLinkIcon className="h-4 w-4 text-[#4FB5E9]" />;
      case 'PHONE_NUMBER':
        return <PhoneIcon className="h-4 w-4 text-[#4FB5E9]" />;
      case 'QUICK_REPLY':
      default:
        return <ReplyIcon className="h-4 w-4 text-[#4FB5E9]" />;
    }
  })();

  return (
    <div
      className={cn(
        'flex w-full items-center justify-center gap-2 border-t border-border bg-background px-4 py-3 text-sm',
        { 'rounded-br-lg rounded-bl-lg': isLast }
      )}
    >
      {icon}
      <span className="font-medium text-[#4FB5E9]">{button.text}</span>
    </div>
  );
};

export const WhatsAppPhonePreview = ({
  template,
  headerParameters = [],
  bodyParameters = [],
  footerParameters = [],
  phoneNumber,
  subscriberContext,
  className,
}: WhatsAppPhonePreviewProps) => {
  const { t } = useTranslation();
  const { header, body, footer, buttons } = getComponentsFromTemplate(template);

  // Cast — resolveWhatsAppParameters handles empty/undefined names gracefully
  const toParams = (params: TemplateParameter[]) => params as Array<{ name: string | Option }>;

  // Resolve template text with current parameter values
  const resolvedHeader = header?.text
    ? resolveWhatsAppParameters(header.text, toParams(headerParameters), subscriberContext)
    : '';
  const resolvedBody = body?.text
    ? resolveWhatsAppParameters(body.text, toParams(bodyParameters), subscriberContext)
    : '';
  const resolvedFooter = footer?.text
    ? resolveWhatsAppParameters(footer.text, toParams(footerParameters), subscriberContext)
    : '';

  const hasContent = resolvedHeader || resolvedBody || resolvedFooter;

  // Header media info
  const headerMedia = (() => {
    if (!header?.format || header.format.toLowerCase() === 'text') {
      return null;
    }
    let exampleUrl: string | undefined;
    if (header.example?.header_handle && Array.isArray(header.example.header_handle)) {
      exampleUrl = header.example.header_handle[0];
    }
    return { format: header.format.toLowerCase() as 'image' | 'video' | 'document', exampleUrl };
  })();

  const templateButtons = buttons || [];

  const messageBubbleClasses = cn('max-w-60 rounded-lg bg-background p-2', {
    'rounded-none rounded-tl-lg rounded-tr-lg': templateButtons.length > 0,
  });

  return (
    <div
      className={cn(
        'h-150 w-80 rounded-[32px] border border-border bg-background p-1.5 shadow-md',
        className
      )}
    >
      <div className="flex h-full w-full flex-col overflow-hidden rounded-[26px] bg-background">
        {/* Header */}
        <div className="flex items-center gap-3 bg-[#F2EEEA] px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-background">
            <Building2Icon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0 flex-1">
            {/* The header sits on fixed light `bg-[#F2EEEA]`, so its text is pinned too. */}
            <div className="truncate text-sm font-semibold text-[#020617]">{phoneNumber}</div>
            <div className="text-xs text-[#48576C]">{t('common.business_account')}</div>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div
          className="flex flex-1 flex-col overflow-y-auto"
          style={{
            backgroundImage: 'url("/assets/campaigns/whatsapp_background.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="p-4">
            {(hasContent || headerMedia) && (
              <div className="flex flex-col">
                <div className={messageBubbleClasses}>
                  {headerMedia && <MediaHeader mediaInfo={headerMedia} />}
                  {hasContent && (
                    <div className="text-sm">
                      {resolvedHeader && (
                        <p className="font-bold whitespace-pre-line">{resolvedHeader}</p>
                      )}
                      {resolvedBody && <p className="whitespace-pre-line">{resolvedBody}</p>}
                      {resolvedFooter && <p className="whitespace-pre-line">{resolvedFooter}</p>}
                    </div>
                  )}
                </div>
                {templateButtons.length > 0 && (
                  <div className="max-w-60 overflow-hidden rounded-br-lg rounded-bl-lg bg-background shadow-sm">
                    {templateButtons.map((button, index) => (
                      <WhatsAppButton
                        key={`${button.type}-${button.text}`}
                        button={button}
                        isLast={index === templateButtons.length - 1}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Input Bar */}
        <div className="flex h-20 items-start gap-2 bg-muted px-3 py-2">
          <PlusIcon className="mt-1.5 h-5 w-5 text-muted-foreground" />
          <div className="flex h-8 w-full flex-1 items-center justify-end rounded-full border border-border bg-muted pr-3">
            <MicIcon className="ml-4 h-5 w-5 text-muted-foreground" />
          </div>
        </div>
      </div>
    </div>
  );
};

// Re-export type for consumers
export type { TemplateParameter };
